/**
 * Theme-aware styles for Recharts and statistics layouts (management dashboard).
 */

import { alpha } from '@mui/material/styles';

export function getRechartsTooltipProps(theme) {
  const isLight = theme.palette.mode === 'light';
  const paper = theme.palette.background.paper;
  const textPrimary = theme.palette.text.primary;
  const bg = isLight ? paper : theme.palette.grey[900];
  const border = isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.12);
  const text = isLight ? textPrimary : theme.palette.text.primary;
  return {
    contentStyle: {
      backgroundColor: bg,
      border: `1px solid ${border}`,
      borderRadius: 8,
      color: text,
      boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, isLight ? 0.12 : 0.4)}`,
      padding: '10px 14px',
    },
    itemStyle: { color: text, fontSize: 13 },
    labelStyle: { color: text, fontWeight: 600, marginBottom: 4 },
  };
}

export function getRechartsTooltipContentStyle(theme) {
  const isLight = theme.palette.mode === 'light';
  return {
    background: isLight ? theme.palette.background.paper : theme.palette.grey[900],
    border: isLight
      ? `1px solid ${theme.palette.divider}`
      : `1px solid ${alpha(theme.palette.common.white, 0.12)}`,
  };
}

export function getRechartsLegendPieProps(theme) {
  const legendColor = theme.palette.text.secondary;
  return {
    wrapperStyle: {
      color: legendColor,
      width: '100%',
      maxHeight: 120,
      overflowX: 'hidden',
      overflowY: 'auto',
      paddingTop: 8,
      lineHeight: 1.6,
      fontSize: 12,
      WebkitOverflowScrolling: 'touch',
    },
    verticalAlign: 'bottom',
    layout: 'horizontal',
    align: 'center',
    iconType: 'circle',
    iconSize: 10,
  };
}

export function getRechartsLegendBarProps(theme) {
  const legendColor = theme.palette.text.secondary;
  return {
    wrapperStyle: { color: legendColor, paddingTop: 8, fontSize: 12 },
    verticalAlign: 'bottom',
    iconType: 'square',
    iconSize: 10,
  };
}

export function getCartesianGridStroke(theme) {
  return theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800];
}

export function getPieSliceStroke(theme) {
  return theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[900];
}

export function getBarTooltipCursor(theme) {
  const isLight = theme.palette.mode === 'light';
  return {
    fill: isLight
      ? alpha(theme.palette.common.black, 0.06)
      : alpha(theme.palette.common.white, 0.05),
  };
}

export function getDemographyChartPaperPieSx(theme) {
  const legendColor = theme.palette.text.primary;
  return {
    overflow: 'visible',
    '& .recharts-wrapper': { overflow: 'visible' },
    '& .recharts-surface': { outline: 'none', overflow: 'visible' },
    '& .recharts-legend-wrapper': { maxWidth: '100%', color: legendColor },
  };
}

export function getDemographyChartPaperBarSx(theme) {
  return {
    overflow: 'hidden',
    '& .recharts-surface': { outline: 'none' },
  };
}

export function getDemographySectionPaperSx(theme) {
  const isLight = theme.palette.mode === 'light';
  if (isLight) {
    return {
      p: { xs: 2, sm: 3 },
      background: theme.palette.grey[50],
      borderRadius: 3,
      border: `1px solid ${theme.palette.divider}`,
    };
  }
  return {
    p: { xs: 2, sm: 3 },
    background: alpha(theme.palette.common.white, 0.02),
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
  };
}

export function getDemographyAccordionSx(theme) {
  const isLight = theme.palette.mode === 'light';
  if (isLight) {
    return {
      background: theme.palette.grey[50],
      borderRadius: '12px !important',
      border: `1px solid ${theme.palette.divider}`,
      '&:before': { display: 'none' },
      '&.Mui-expanded': { margin: '0 !important' },
      overflow: 'hidden',
    };
  }
  return {
    background: alpha(theme.palette.common.white, 0.02),
    borderRadius: '12px !important',
    border: `1px solid ${alpha(theme.palette.common.white, 0.06)}`,
    '&:before': { display: 'none' },
    '&.Mui-expanded': { margin: '0 !important' },
    overflow: 'hidden',
  };
}

/** KPI cards and chart panels that used a fixed dark grey surface */
export function getStatisticsSurfaceBackground(theme) {
  return theme.palette.mode === 'light'
    ? theme.palette.grey[100]
    : theme.palette.background.paper;
}

/** Slightly recessed panels (e.g. nested chart cards) */
export function getStatisticsAltSurfaceBackground(theme) {
  return theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800];
}

/** Deepest nested mini panels */
export function getStatisticsDeepSurfaceBackground(theme) {
  return theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[900];
}

/** Tooltip / bar cursor highlight on chart (Recharts cursor fill) */
export function getStatisticsChartCursorFill(theme) {
  const isLight = theme.palette.mode === 'light';
  return isLight
    ? alpha(theme.palette.common.black, 0.08)
    : alpha(theme.palette.common.white, 0.08);
}

export function getRechartsAxisStroke(theme) {
  return theme.palette.mode === 'light' ? theme.palette.grey[600] : theme.palette.grey[500];
}

export function getRechartsTooltipLabelStyle(theme) {
  return { color: theme.palette.text.primary };
}

/** Inline table row top border in statistics HTML tables */
export function getStatisticsTableRowBorderTop(theme) {
  return `1px solid ${
    theme.palette.mode === 'light' ? theme.palette.divider : theme.palette.grey[800]
  }`;
}

export function getStatisticsKpiLabelColor(theme) {
  return theme.palette.text.secondary;
}

export function getStatisticsKpiHelperColor(theme) {
  return theme.palette.mode === 'light'
    ? theme.palette.text.secondary
    : theme.palette.text.disabled;
}

/** Outer wrapper for statistics views that use a plain div (native headings inherit theme text color). */
export function getStatisticsPageRootStyle(theme) {
  return {
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.25rem',
    color: theme.palette.text.primary,
  };
}

/** MUI sx fragment: primary text on view root Box containers. */
export const viewRootTextPrimarySx = { color: 'text.primary' };

/** Custom tooltip / filter boxes that used #1f1f1f + #333 border */
export function getStatisticsTooltipBoxSx(theme) {
  const isLight = theme.palette.mode === 'light';
  return {
    background: isLight ? theme.palette.grey[100] : theme.palette.grey[900],
    border: `1px solid ${
      isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.12)
    }`,
    borderRadius: 1,
  };
}

/** MUI TableRow sx for totals ("Gesamt") rows in report tables */
export function getStatisticsTotalsRowSx(theme) {
  const bg =
    theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800];
  return {
    bgcolor: bg,
    '& td': { color: theme.palette.getContrastText(bg) },
  };
}

/** MUI TableRow sx for section / group header rows (lighter than totals in light mode) */
export function getStatisticsSectionHeaderRowSx(theme) {
  const bg =
    theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800];
  return {
    bgcolor: bg,
    '& td': { color: theme.palette.getContrastText(bg) },
  };
}

/**
 * Inline styles for <td> cells in native HTML table totals rows (same visual as getStatisticsTotalsRowSx).
 */
export function getStatisticsTotalsHtmlCellStyle(theme, extra = {}) {
  const bg =
    theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[800];
  const { color: colorOverride, ...rest } = extra;
  return {
    padding: '0.75rem',
    borderTop: `2px solid ${theme.palette.divider}`,
    backgroundColor: bg,
    fontWeight: 700,
    color: colorOverride ?? theme.palette.getContrastText(bg),
    ...rest,
  };
}

/** Native <th> cells in statistics HTML tables (aligned with demography table header treatment). */
export function getStatisticsHtmlThStyle(theme, extra = {}) {
  const isLight = theme.palette.mode === 'light';
  return {
    padding: '0.75rem',
    fontWeight: 600,
    textAlign: 'left',
    backgroundColor: isLight ? theme.palette.grey[100] : alpha(theme.palette.common.white, 0.04),
    borderBottom: isLight
      ? `2px solid ${theme.palette.divider}`
      : `2px solid ${alpha(theme.palette.common.white, 0.08)}`,
    color: theme.palette.text.primary,
    ...extra,
  };
}

export function getDemographyTableHeaderCellSx(theme) {
  const isLight = theme.palette.mode === 'light';
  if (isLight) {
    return {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
      borderBottom: `2px solid ${theme.palette.divider}`,
      fontSize: '0.8rem',
      letterSpacing: '0.02em',
    };
  }
  return {
    fontWeight: 600,
    backgroundColor: alpha(theme.palette.common.white, 0.04),
    borderBottom: `2px solid ${alpha(theme.palette.common.white, 0.08)}`,
    fontSize: '0.8rem',
    letterSpacing: '0.02em',
  };
}

/** Filter card above statistic views (StatisticViewShell). */
export function getStatisticViewShellFilterPaperSx(theme) {
  const isLight = theme.palette.mode === 'light';
  return {
    mb: { xs: 3, md: 4 },
    p: { xs: 2, sm: 2.5, md: 3 },
    borderRadius: 3,
    border: `1px solid ${
      isLight ? theme.palette.divider : alpha(theme.palette.common.white, 0.08)
    }`,
    backgroundColor: isLight ? theme.palette.grey[50] : alpha(theme.palette.common.white, 0.02),
  };
}

/** Plain accordion used for collapsible tables on statistic pages. */
export function getStatisticsPlainAccordionSx(theme) {
  const isLight = theme.palette.mode === 'light';
  return {
    bgcolor: isLight ? alpha(theme.palette.common.black, 0.03) : alpha(theme.palette.common.white, 0.03),
  };
}

/** Alternating row background for dense statistic tables. */
export function getStatisticsZebraTableRowSx(theme, idx) {
  const isLight = theme.palette.mode === 'light';
  const stripe = isLight
    ? alpha(theme.palette.common.black, 0.03)
    : alpha(theme.palette.common.white, 0.015);
  const hover = isLight
    ? alpha(theme.palette.common.black, 0.06)
    : alpha(theme.palette.common.white, 0.04);
  return {
    backgroundColor: idx % 2 === 0 ? 'transparent' : stripe,
    '&:hover': { backgroundColor: `${hover} !important` },
  };
}
